import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';

import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import { useTranslation } from '../../../../shared/i18n';
import { trailingRowDisclosureChevron } from '../../../../shared/i18n/rtl';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { appTheme, type ThemeColors } from '../../../../shared/theme';
import { hexToRgba } from '../../../../shared/theme/colorUtils';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import {
  formatAddressSummaryLine,
  getAreaBilingualLabel,
  useAddresses,
  useAreas,
  useSetDefaultAddress,
  type Address,
} from '../../../../data/domains/addresses';
import { useAuthStore } from '../../../../shared/auth/store';
import { Clickable } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { ButtonLoader } from '../../../../components/load-main/ButtonLoader';
import { DashboardCardShellSet2 } from './DashboardCardShellSet2';
import { dashboardSet2NestedTileShadow } from './dashboardSet2Styles';

const LIST_GAP = appTheme.spacing.sm;
const ROW_PADDING_V = appTheme.spacing.sm;
const ROW_PADDING_H = appTheme.spacing.md;
const ROW_RADIUS = 14;
const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export interface DashboardAddressSetupSet2Props {
  colors: ThemeColors;
}

function resolveAddressAreaLabel(address: Address, areas: Array<Record<string, unknown>>): string {
  const row = address as Address & { area?: unknown };
  if (address.area_name != null && String(address.area_name).trim() !== '') {
    return String(address.area_name).trim();
  }
  if (row.area != null && typeof row.area === 'object' && !Array.isArray(row.area)) {
    const from_nested = getAreaBilingualLabel(row.area as Record<string, unknown>);
    if (from_nested.trim() !== '') {
      return from_nested.trim();
    }
  }
  const match = areas.find(a => Number(a.id) === Number(address.area_id));
  if (match) {
    return getAreaBilingualLabel(match) || (typeof match.name === 'string' ? match.name : '') || '';
  }
  return '';
}

export function DashboardAddressSetupSet2({ colors }: DashboardAddressSetupSet2Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const navigation = useAuthNavigation();
  const customer_id = useAuthStore(s => s.user?.id);
  const { data: addresses, isLoading } = useAddresses();
  const { data: areas_data } = useAreas();
  const areas = useMemo(
    () => (Array.isArray(areas_data) ? (areas_data as Array<Record<string, unknown>>) : []),
    [areas_data],
  );
  const { mutateAsync: setDefault, isPending: setting_default } = useSetDefaultAddress();
  const has_addresses = (addresses?.length ?? 0) > 0;
  const default_tint = (hexToRgba(colors.primary, 0.08) as string) || colors.primaryFade;
  const badge_tint = (hexToRgba(colors.primary, 0.16) as string) || colors.primaryFade;

  const format_address_line = useCallback(
    (address: Address) => {
      const area_label = resolveAddressAreaLabel(address, areas);
      return formatAddressSummaryLine(
        {
          ...(address as unknown as Record<string, unknown>),
          area_name: area_label || address.area_name,
        },
        t,
      );
    },
    [areas, t],
  );

  const on_set_default = useCallback(
    async (address: Address) => {
      if (address.is_default || typeof customer_id !== 'number') {
        return;
      }
      await setDefault({ customerId: customer_id, addressId: address.id });
    },
    [customer_id, setDefault],
  );

  const on_add_new = useCallback(() => {
    navigation.navigate('AddressForm');
  }, [navigation]);

  const on_go_to_list = useCallback(() => {
    navigation.navigate('AddressLists');
  }, [navigation]);

  return (
    <DashboardCardShellSet2 colors={colors} no_padding>
      <View style={styles.titleRow}>
        <View style={styles.titleShell}>
          <Text
            style={[styles.title, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
            numberOfLines={1}>
            {t('dashboard.addresses')}
          </Text>
        </View>
        {has_addresses ? (
          <Clickable onPress={on_go_to_list} style={styles.viewAllClickable} accessibilityLabel={t('dashboard.viewAll')} accessibilityRole="button">
            <View style={styles.viewAllWrap}>
              <Text style={[styles.viewAll, { color: colors.primary }, ANDROID_TEXT]}>{t('dashboard.viewAll')}</Text>
              <SvgIcon name={trailingRowDisclosureChevron()} size={16} color={colors.primary} />
            </View>
          </Clickable>
        ) : null}
      </View>

      {isLoading ? (
        <View style={styles.loaderWrap}>
          <ButtonLoader size={20} color={colors.primary} />
        </View>
      ) : has_addresses ? (
        <View style={styles.list}>
          {(addresses ?? []).map(address => (
            <Clickable
              key={address.id}
              onPress={() => on_set_default(address)}
              disabled={address.is_default || setting_default}
              style={styles.rowWrap}
              accessibilityLabel={address.is_default ? t('address.default') : t('address.setDefault')}
              accessibilityRole="button">
              <View
                style={[
                  styles.row,
                  dashboardSet2NestedTileShadow(colors),
                  {
                    backgroundColor: address.is_default ? default_tint : colors.surfaceElevated,
                    shadowColor: colors.shadowColor,
                  },
                  address.is_default && styles.rowDefault,
                  address.is_default && { borderColor: colors.primary },
                ]}>
                <SvgIcon name="location" size={20} color={colors.primary} style={styles.rowIcon} />
                <View style={styles.rowText}>
                  <View style={styles.rowTitleRow}>
                    <Text
                      style={[styles.rowTitle, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                      numberOfLines={1}>
                      {address.name || t('address.addressFallback')}
                    </Text>
                    {address.is_default ? (
                      <View style={[styles.defaultBadge, { backgroundColor: badge_tint }]}>
                        <Text style={[styles.defaultBadgeText, { color: colors.primary }, ANDROID_TEXT]}>{t('address.default')}</Text>
                      </View>
                    ) : null}
                  </View>
                  <Text
                    style={[styles.rowSubtitle, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                    numberOfLines={2}>
                    {format_address_line(address)}
                  </Text>
                </View>
                {!address.is_default ? <SvgIcon name={trailingRowDisclosureChevron()} size={18} color={colors.textLight} /> : null}
              </View>
            </Clickable>
          ))}
          <Clickable onPress={on_add_new} style={styles.addWrap} accessibilityLabel={t('address.addNew')} accessibilityRole="button">
            <View style={[styles.addRow, dashboardSet2NestedTileShadow(colors), { backgroundColor: colors.surfaceElevated, shadowColor: colors.shadowColor }]}>
              <SvgIcon name="add" size={20} color={colors.primary} />
              <Text style={[styles.addRowText, { color: colors.primary }, ANDROID_TEXT]}>{t('address.addNew')}</Text>
            </View>
          </Clickable>
        </View>
      ) : (
        <View style={styles.emptyWrap}>
          <Text
            style={[styles.emptyText, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
            {t('checkout.setDefaultAddressMessage')}
          </Text>
          <Clickable onPress={on_add_new} style={styles.emptyButtonWrap} accessibilityLabel={t('address.addNew')} accessibilityRole="button">
            <View style={[styles.emptyButton, { backgroundColor: colors.primary }]}>
              <SvgIcon name="add" size={20} color={colors.onPrimary} />
              <Text style={[styles.emptyButtonText, { color: colors.onPrimary }, ANDROID_TEXT]}>{t('address.addNew')}</Text>
            </View>
          </Clickable>
        </View>
      )}
    </DashboardCardShellSet2>
  );
}

const styles = StyleSheet.create({
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingBottom: LIST_GAP,
    paddingTop: DASH_STUDIO_INNER,
    gap: appTheme.spacing.sm,
  },
  titleShell: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  title: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
  },
  viewAllClickable: {
    flexShrink: 0,
  },
  viewAllWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.xxs,
    flexShrink: 0,
  },
  viewAll: {
    fontSize: 12,
    fontWeight: '700',
  },
  loaderWrap: {
    paddingVertical: appTheme.spacing.xl,
    alignItems: 'center',
  },
  list: {
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingBottom: DASH_STUDIO_INNER,
    gap: LIST_GAP,
  },
  rowWrap: {},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ROW_PADDING_V,
    paddingHorizontal: ROW_PADDING_H,
    borderRadius: ROW_RADIUS,
  },
  rowDefault: {
    borderWidth: 1,
  },
  rowIcon: {
    marginEnd: appTheme.spacing.sm,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.sm,
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  rowTitle: {
    ...appTheme.typography.bodyMedium,
    fontWeight: '600',
    flexShrink: 1,
  },
  defaultBadge: {
    paddingHorizontal: appTheme.spacing.xs,
    paddingVertical: 2,
    borderRadius: appTheme.borderRadius.xs,
    alignSelf: 'flex-start',
  },
  defaultBadgeText: {
    ...appTheme.typography.captionSmall,
    fontWeight: '600',
  },
  rowSubtitle: {
    ...appTheme.typography.bodySmall,
  },
  addWrap: {
    marginTop: 2,
    borderRadius: ROW_RADIUS,
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
    paddingVertical: ROW_PADDING_V,
    paddingHorizontal: 0,
    borderRadius: ROW_RADIUS,
  },
  addRowText: {
    ...appTheme.typography.bodyMedium,
    fontWeight: '600',
  },
  emptyWrap: {
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingVertical: DASH_STUDIO_INNER,
    alignItems: 'center',
    gap: appTheme.spacing.md,
  },
  emptyText: {
    ...appTheme.typography.bodySmall,
  },
  emptyButtonWrap: {},
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: DASH_STUDIO_INNER,
    borderRadius: ROW_RADIUS,
  },
  emptyButtonText: {
    ...appTheme.typography.bodyMedium,
    fontWeight: '600',
  },
});
